// utils/investmentCalculations.js

const DAY_BASIS = 365;

export const COMPOUND_FREQUENCIES = {
  daily: 365,
  weekly: 52,
  bi_weekly: 26,
  monthly: 12,
  quarterly: 4,
  yearly: 1,
};

export const calculateSimpleInterest = (
  principal,
  annualRatePercent,
  days,
  dayBasis = DAY_BASIS
) => {
  const rate = Number(annualRatePercent || 0) / 100;
  const principalAmount = Number(principal || 0);
  return principalAmount * rate * (Number(days || 0) / dayBasis);
};

export const calculateCompoundInterest = (
  principal,
  annualRatePercent,
  days,
  frequency = "monthly",
  dayBasis = DAY_BASIS
) => {
  const principalAmount = Number(principal || 0);
  const rate = Number(annualRatePercent || 0) / 100;
  const periodsPerYear = COMPOUND_FREQUENCIES[frequency] || COMPOUND_FREQUENCIES.monthly;
  const years = Number(days || 0) / dayBasis;
  const amount = principalAmount * Math.pow(1 + rate / periodsPerYear, periodsPerYear * years);
  return amount - principalAmount;
};

export const calculateInvestmentInterest = ({
  principal,
  annualRatePercent,
  days,
  interestType,
  compoundFrequency,
}) => {
  if (interestType === "compound") {
    return calculateCompoundInterest(
      principal,
      annualRatePercent,
      days,
      compoundFrequency
    );
  }
  return calculateSimpleInterest(principal, annualRatePercent, days);
};

export const calculateMaturityAmounts = ({
  principal,
  annualRatePercent,
  days,
  interestType,
  compoundFrequency,
  withholdingTaxRate = 15,
}) => {
  const expectedInterest = calculateInvestmentInterest({
    principal,
    annualRatePercent,
    days,
    interestType,
    compoundFrequency,
  });

  const grossMaturity = Number(principal || 0) + expectedInterest;
  const taxRate = Number(withholdingTaxRate || 0) / 100;
  const withholdingTax = expectedInterest * taxRate;
  const netInterest = expectedInterest - withholdingTax;
  const netMaturity = Number(principal || 0) + netInterest;

  return {
    expectedInterest,
    grossMaturity,
    withholdingTax,
    netInterest,
    netMaturity,
  };
};

export const calculateAccruedValue = ({
  principal,
  annualRatePercent,
  startDate,
  asOfDate,
  interestType,
  compoundFrequency,
}) => {
  if (!startDate) return Number(principal || 0);
  const start = new Date(startDate);
  const end = asOfDate ? new Date(asOfDate) : new Date();
  const days = Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)));
  const accruedInterest = calculateInvestmentInterest({
    principal,
    annualRatePercent,
    days,
    interestType,
    compoundFrequency,
  });
  return Number(principal || 0) + accruedInterest;
};

export const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
